import { TextOverlay, CaptionCue } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { cleanVoiceoverScript } from './cleanVoiceoverScript';

export interface BuildCaptionOverlaysOptions {
  /** Caption visual preset, mapped onto each overlay's `design`. Defaults to 'classic'. */
  design?: TextOverlay['design'];
  /** Hard cap: characters per caption line before forcing a break. Default 42. */
  maxCharsPerLine?: number;
  /** Hard cap: words per caption line before forcing a break. Default 7. */
  maxWordsPerLine?: number;
  /** Preferred words per caption line. Lines aim for this; sentence ends can flush earlier. Default 5. */
  targetWordsPerLine?: number;
  /** A line shorter than this (in words) is merged into a neighbour instead of standing alone. Default 2. */
  minWordsPerLine?: number;
  /**
   * Seconds to reveal each caption *before* its computed start, so captions land on or a hair
   * before the spoken word rather than lagging. Default 0.1. The first caption clamps at 0.
   */
  leadInSec?: number;
  /**
   * Known trailing silence/padding at the END of the base video (seconds). When the base video
   * has dead air after the narration, set this so captions pace against the spoken window and
   * don't stretch slow. Default 0 (HeyGen avatar videos are ~all speech).
   */
  trailingSilenceSec?: number;
}

/** Trailing sentence-final punctuation (optionally followed by a closing quote/bracket). */
const SENTENCE_END = /[.!?…]["')\]]?$/;

/**
 * Build editable caption overlays from the *verbatim* spoken script.
 *
 * Why this exists: when captions are toggled on without supplying caption text, the backend
 * (Creatomate) auto-transcribes the avatar audio with speech-to-text, which introduces errors.
 * The exact words the avatar speaks are already known in the frontend (the script), so we turn
 * that script into caption-sized `TextOverlay`s instead. They render through the existing
 * `text_overlays` assembly path (no backend change), are shown on the timeline, and can be
 * hand-edited line by line.
 *
 * Timing model (why captions used to drift / lag):
 *  - Lines are grouped into *balanced* chunks (≈targetWordsPerLine) and only broken at a
 *    sentence boundary once a line has enough words — so we don't emit lonely "Welcome!"
 *    fragments that throw the rhythm off.
 *  - Each caption is then placed by its *cumulative* share of the total caption weight
 *    (character count ≈ time-to-speak), anchored to its absolute position in the narration:
 *    `start = (weightBefore / totalWeight) * window`. Because every start is derived from the
 *    cumulative fraction — not from summing per-line durations with a minimum-duration floor —
 *    rounding/floor error can't accumulate, so captions no longer fall progressively behind the
 *    audio. A small lead-in shows each caption a hair early so it never reads as late.
 */
export const buildCaptionOverlays = (
  script: string,
  durationSeconds: number,
  options: BuildCaptionOverlaysOptions = {}
): TextOverlay[] => {
  const {
    design = 'classic',
    maxCharsPerLine = 42,
    maxWordsPerLine = 7,
    targetWordsPerLine = 5,
    minWordsPerLine = 2,
    leadInSec = 0.1,
    trailingSilenceSec = 0,
  } = options;

  const spoken = cleanVoiceoverScript(script || '')
    // Captions read as one continuous track; collapse the script's line breaks to spaces.
    .replace(/\s+/g, ' ')
    .trim();
  if (!spoken) return [];

  const words = spoken.split(' ').filter(Boolean);
  if (words.length === 0) return [];

  const totalDuration = durationSeconds > 0 ? durationSeconds : 60;

  // ── 1. Balanced grouping ───────────────────────────────────────────────────
  // Accumulate words into a line; flush on a hard cap, or at a sentence end once the line
  // already carries a sensible number of words (so single-word sentences don't stand alone).
  const lines: string[] = [];
  let current: string[] = [];
  let currentChars = 0;
  // Allow a sentence-end break a little before the target so lines end on natural pauses
  // without becoming tiny. e.g. target 5 → break allowed from 3 words.
  const minWordsForSentenceBreak = Math.max(minWordsPerLine, Math.ceil(targetWordsPerLine * 0.6));

  const flush = () => {
    if (current.length) {
      lines.push(current.join(' '));
      current = [];
      currentChars = 0;
    }
  };

  for (const word of words) {
    const projectedChars = current.length === 0 ? word.length : currentChars + 1 + word.length;
    const wouldOverflow = current.length >= maxWordsPerLine || projectedChars > maxCharsPerLine;

    // Start a new line before adding when the current one is full.
    if (wouldOverflow && current.length > 0) flush();

    current.push(word);
    currentChars = current.length === 1 ? word.length : currentChars + 1 + word.length;

    const reachedHardCap = current.length >= maxWordsPerLine || currentChars >= maxCharsPerLine;
    const naturalPause = SENTENCE_END.test(word) && current.length >= minWordsForSentenceBreak;
    if (reachedHardCap || naturalPause) flush();
  }
  flush();

  if (lines.length === 0) return [];

  // Merge a lonely trailing fragment (e.g. a dangling "you know.") into the previous caption
  // so we never show a too-short final line.
  if (lines.length >= 2) {
    const last = lines[lines.length - 1];
    if (last.split(' ').length < minWordsPerLine) {
      lines[lines.length - 2] = `${lines[lines.length - 2]} ${last}`;
      lines.pop();
    }
  }

  // ── 2. Anchored, cumulative timing ─────────────────────────────────────────
  // Spread captions across the spoken window (full video minus any known trailing silence).
  const window = Math.max(0.5, totalDuration - Math.max(0, trailingSilenceSec));
  // Character count per line ≈ time to speak it (better than word count for varied word lengths).
  const weights = lines.map((l) => Math.max(1, l.length));
  const totalWeight = weights.reduce((sum, w) => sum + w, 0);

  const overlays: TextOverlay[] = [];
  let cumulative = 0;

  lines.forEach((text, index) => {
    const startFrac = cumulative / totalWeight;
    cumulative += weights[index];
    const endFrac = cumulative / totalWeight;

    // Lead-in shifts every caption slightly earlier; the first clamps at 0.
    const start = Math.max(0, startFrac * window - leadInSec);
    const end = Math.min(totalDuration, endFrac * window - (index === lines.length - 1 ? 0 : leadInSec));
    const duration = Math.max(0.2, end - start);

    overlays.push({
      id: uuidv4(),
      text,
      time: Number(start.toFixed(2)),
      duration: Number(duration.toFixed(2)),
      style: 'subtitle',
      position: 'bottom',
      design,
      // No default animation — captions appear instantly. Users opt in per-overlay
      // or via the editor's "Apply to all" control.
      animation: 'none',
      animation_out: 'none',
      source: 'caption',
    });
  });

  return overlays;
};

export interface BuildCaptionOverlaysFromCuesOptions {
  design?: TextOverlay['design'];
  targetWordsPerLine?: number;
  minWordsPerLine?: number;
  leadInSec?: number;
  /** How long a caption lingers after its last spoken word, seconds. Default 0.4. */
  holdAfterSec?: number;
  /** Readability floor: a caption stays on screen at least this long, seconds. Default 0.7. */
  minOnScreenSec?: number;
}

/**
 * Build caption overlays from real word-level timestamps (Whisper STT / ElevenLabs cues).
 * Same balanced line-grouping as buildCaptionOverlays, but each line's time/duration comes
 * from the actual word start/end — no estimation.
 *
 * Timing model (why captions used to overlap on the timeline): a caption spans from its own
 * first word (minus a lead-in) until the NEXT caption's first word (minus the same lead-in),
 * so exactly one caption is on screen at a time — no overlaps, no gaps — with two guards:
 *  - it never lingers more than `holdAfterSec` past its own last word (long pauses clear the
 *    caption instead of stretching it), and
 *  - it stays up at least `minOnScreenSec` for readability.
 * Whisper word timestamps are occasionally non-monotonic, so line bounds are clamped to a
 * strictly increasing sequence first.
 */
export const buildCaptionOverlaysFromCues = (
  cues: CaptionCue[],
  options: BuildCaptionOverlaysFromCuesOptions = {}
): TextOverlay[] => {
  if (!cues.length) return [];

  const {
    design = 'classic',
    targetWordsPerLine = 5,
    minWordsPerLine = 2,
    leadInSec = 0.1,
    holdAfterSec = 0.4,
    minOnScreenSec = 0.7,
  } = options;

  const minWordsForSentenceBreak = Math.max(minWordsPerLine, Math.ceil(targetWordsPerLine * 0.6));

  // Group cues into balanced lines, same sentence-break heuristic as buildCaptionOverlays.
  const lines: CaptionCue[][] = [];
  let current: CaptionCue[] = [];

  for (const cue of cues) {
    current.push(cue);
    const reachedTarget = current.length >= targetWordsPerLine;
    const naturalPause = SENTENCE_END.test(cue.word) && current.length >= minWordsForSentenceBreak;
    if (reachedTarget || naturalPause) {
      lines.push(current);
      current = [];
    }
  }
  if (current.length) lines.push(current);

  // Merge lonely trailing fragment into the previous line.
  if (lines.length >= 2 && lines[lines.length - 1].length < minWordsPerLine) {
    const tail = lines.pop()!;
    lines[lines.length - 1] = [...lines[lines.length - 1], ...tail];
  }

  // Raw [start, end] per line, forced into a strictly increasing sequence (Whisper word
  // timestamps can jitter backwards, which is what produced overlapping caption blocks).
  const bounds = lines.map((lineCues) => ({
    start: lineCues[0].start,
    end: lineCues[lineCues.length - 1].end,
    text: lineCues.map((c) => c.word).join(' '),
  }));
  for (let i = 0; i < bounds.length; i++) {
    if (i > 0 && bounds[i].start < bounds[i - 1].end) bounds[i].start = bounds[i - 1].end;
    if (bounds[i].end < bounds[i].start + 0.05) bounds[i].end = bounds[i].start + 0.05;
  }

  return bounds.map((line, index) => {
    const next = bounds[index + 1];
    const start = Math.max(0, line.start - leadInSec);
    // When the next caption appears (its own lead-in applied). No next → open-ended.
    const nextStart = next ? Math.max(start, next.start - leadInSec) : Infinity;

    // End when the next caption takes over, but don't linger far past this line's own audio.
    let end = Math.min(line.end + holdAfterSec, nextStart);
    // Keep it on screen long enough to read — but never past where the next caption starts,
    // so captions can't overlap even for pathologically fast lines.
    end = Math.max(end, Math.min(start + minOnScreenSec, nextStart));

    const duration = Math.max(0.15, end - start);

    return {
      id: uuidv4(),
      text: line.text,
      time: Number(start.toFixed(2)),
      duration: Number(duration.toFixed(2)),
      style: 'subtitle',
      position: 'bottom',
      design,
      animation: 'none',
      animation_out: 'none',
      source: 'caption',
    } satisfies TextOverlay;
  });
};
